import { NavLink, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import {
  LayoutDashboard, Image as ImageIcon, Inbox, Settings,
  Home, Users, Newspaper, Boxes, Tag, Briefcase, Store, Plus, CalendarDays, MessageSquareQuote, QrCode, Bot, ChevronDown, Globe, Search, type LucideIcon,
} from 'lucide-react';
import { api } from '../../lib/api';
import type { CollectionTypeDef } from '../../lib/types';
import { pickLoc } from '../../lib/collections';
import AddContentModal from '../forms/AddContentModal';
import { panelMeta } from '../../lib/panelMeta';
import { coreNavItems } from '../../lib/panelNavigation';
import VideroMark from './VideroMark';

// Iconos por nombre (lucide) con fallback por arquetipo y, en último caso, Boxes.
const ICON_BY_NAME: Record<string, LucideIcon> = {
  home: Home, users: Users, newspaper: Newspaper, tag: Tag,
  briefcase: Briefcase, store: Store, boxes: Boxes,
};
const ICON_BY_ARCHETYPE: Record<string, LucideIcon> = {
  listing: Home, people: Users, editorial: Newspaper,
  catalog: Store, social: Tag, ops: Briefcase,
};
function iconFor(t: CollectionTypeDef): LucideIcon {
  return (t.icon && ICON_BY_NAME[t.icon]) || ICON_BY_ARCHETYPE[t.archetype] || Boxes;
}

interface TenantSummary { id: string; name: string; slug: string; domain: string | null }

interface SidebarProps {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
  isSaasAdmin?: boolean;
  tenants?: TenantSummary[];
  activeTenantId?: string | null;
  activeTenantName?: string | null;
  onTenantSwitch?: (id: string) => void;
}

export default function Sidebar({
  mobileOpen = false,
  onMobileClose,
  isSaasAdmin = false,
  tenants = [],
  activeTenantId = null,
  activeTenantName = null,
  onTenantSwitch,
}: SidebarProps) {
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [types, setTypes] = useState<CollectionTypeDef[]>([]);
  const [addOpen, setAddOpen] = useState(false);

  const loadTypes = useCallback(() => {
    return api.collectionTypes.list()
      .then((t) => setTypes([...t].sort((a, b) => a.sortOrder - b.sortOrder)))
      .catch(() => { /* sin colecciones aún */ });
  }, []);

  useEffect(() => { loadTypes(); }, [loadTypes]);

  async function handleAdded(createdKeys: string[]) {
    setAddOpen(false);
    await loadTypes();
    if (createdKeys[0]) navigate(`/c/${createdKeys[0]}`);
  }

  useEffect(() => {
    const load = () =>
      api.submissions.unreadCount().then((r) => setUnread(r.count)).catch(() => { /* ignore */ });
    load();
    const onRead = () => setUnread(0);
    window.addEventListener('submissions:read', onRead);
    const interval = setInterval(load, 60_000);
    return () => {
      window.removeEventListener('submissions:read', onRead);
      clearInterval(interval);
    };
  }, []);

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
      isActive
        ? 'bg-ui-accent/15 text-ui-text border border-ui-accent/30'
        : 'text-ui-text-muted hover:text-ui-text hover:bg-ui-text/[0.06]'
    }`;

  return (
    <>
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 md:hidden"
          onClick={onMobileClose}
        />
      )}
    <aside className={[
      'w-56 bg-ui-surface border-r border-ui-border flex flex-col',
      'md:relative md:shrink-0 md:translate-x-0 md:inset-auto md:z-auto',
      'fixed inset-y-0 left-0 z-50 transition-transform duration-200',
      mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
    ].join(' ')}>
      <div className="h-14 flex items-center px-2 border-b border-ui-border">
        <span className="flex items-center gap-2 min-w-0">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-ui-accent/12 text-ui-accent">
            <VideroMark className="h-5 w-5" />
          </span>
          <span className="font-semibold text-sm tracking-wider text-ui-text">{panelMeta.productName}</span>
        </span>
        <span className="ml-2 max-w-[7.5rem] truncate text-xs font-medium text-ui-accent bg-ui-accent/10 px-1.5 py-0.5 rounded">
          {activeTenantName || 'Sitio'}
        </span>
      </div>

      {/* Tenant switcher — solo para saas_admin con más de un tenant */}
      {isSaasAdmin && tenants.length > 1 && (
        <div className="px-3 py-2 border-b border-ui-border">
          <div className="relative">
            <select
              value={activeTenantId ?? ''}
              onChange={(e) => onTenantSwitch?.(e.target.value)}
              className="w-full appearance-none rounded-md border border-ui-border bg-ui-bg px-3 py-1.5 pr-7 text-sm text-ui-text focus:outline-none focus:border-ui-accent cursor-pointer"
            >
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}{t.domain ? ` · ${t.domain}` : ''}
                </option>
              ))}
            </select>
            <ChevronDown size={13} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-ui-text-subtle" />
          </div>
        </div>
      )}

      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto" onClick={onMobileClose}>
        {coreNavItems
          .filter((item) => ['dashboard', 'site', 'bookings', 'reputation', 'chatbot'].includes(item.id))
          .map((item) => {
            const Icon =
              item.id === 'dashboard' ? LayoutDashboard :
              item.id === 'site' ? Globe :
              item.id === 'bookings' ? CalendarDays :
              item.id === 'reputation' ? MessageSquareQuote :
              Bot;

            return (
              <NavLink key={item.id} to={item.to} end={item.id === 'dashboard'} className={linkClass}>
                <span className="w-4 flex-shrink-0"><Icon size={15} /></span>
                {item.label}
              </NavLink>
            );
          })}

        {/* Colecciones dinámicas (motor genérico) */}
        {types.length > 0 && (
          <p className="px-3 pt-4 pb-1 text-xs font-semibold uppercase tracking-wider text-ui-text-subtle">
            Secciones de contenido
          </p>
        )}
        {types.map((t) => {
          const Icon = iconFor(t);
          const label = pickLoc(t.labels.plural, 'es') || t.key;
          return (
            <NavLink key={t.id} to={`/c/${t.key}`} className={linkClass}>
              <span className="w-4 flex-shrink-0"><Icon size={15} /></span>
              {label}
            </NavLink>
          );
        })}
        <button
          type="button"
          onClick={() => setAddOpen(true)}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-ui-text-subtle hover:text-ui-text hover:bg-ui-text/[0.06] transition-colors"
        >
          <span className="w-4 flex-shrink-0"><Plus size={15} /></span>
          Añadir sección
        </button>

        <div className="pt-4 space-y-0.5">
          {coreNavItems
            .filter((item) => ['media', 'qr'].includes(item.id))
            .map((item) => {
              const Icon = item.id === 'media' ? ImageIcon : QrCode;
              return (
                <NavLink key={item.id} to={item.to} className={linkClass}>
                  <span className="w-4 flex-shrink-0"><Icon size={15} /></span>
                  {item.label}
                </NavLink>
              );
            })}
          <NavLink to="/submissions" className={linkClass}>
            {({ isActive }) => (
              <>
                <span className={`w-4 flex-shrink-0 ${isActive ? 'text-ui-accent' : ''}`}><Inbox size={15} /></span>
                {coreNavItems.find((item) => item.id === 'submissions')?.label}
                {unread > 0 && (
                  <span className="ml-auto text-xs font-semibold leading-none bg-ui-accent text-ui-bg rounded-full px-1.5 py-1 min-w-[18px] text-center">
                    {unread > 99 ? '99+' : unread}
                  </span>
                )}
              </>
            )}
          </NavLink>
          {coreNavItems
            .filter((item) => ['seo', 'settings'].includes(item.id))
            .map((item) => {
              const Icon = item.id === 'seo' ? Search : Settings;
              return (
                <NavLink key={item.id} to={item.to} className={linkClass}>
                  <span className="w-4 flex-shrink-0"><Icon size={15} /></span>
                  {item.label}
                </NavLink>
              );
            })}
        </div>
      </nav>

      {addOpen && (
        <AddContentModal
          existingKeys={types.map((t) => t.key)}
          onClose={() => setAddOpen(false)}
          onAdded={handleAdded}
        />
      )}
    </aside>
    </>
  );
}
